import React, { CSSProperties } from 'react';
import {
  Button as MuiButton,
  SxProps,
  Theme,
  type ButtonProps as MuiButtonProps,
} from '@mui/material';
import { Link as RouterLink, type To } from 'react-router-dom';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { ghostButtonLabelStyles } from '../ghost-button.styles';

type ButtonColor = Extract<
  NonNullable<MuiButtonProps['color']>,
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'neutral'
  | 'inherit'
>;
const textAlignedVariant = 'text-aligned';

export type BaseButtonProps = {
  variant?: MuiButtonProps['variant'] | typeof textAlignedVariant;
  size?: MuiButtonProps['size'] | 'tiny';
  color?: ButtonColor;
  disabled?: boolean;
  textTransform?: 'uppercase' | 'none';
  type?: 'button' | 'submit' | 'reset';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  id?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  children: React.ReactNode;
  /** Optional router link semantics (renders RouterLink under the hood) */
  link?: { to: To; replace?: boolean; target?: string; rel?: string };
  /** Min width. Apply 0 if button is in a grid and contains ellipsis text */
  minWidth?: CSSProperties['minWidth'];
  /** Full width */
  fullWidth?: boolean;
  /**
   * Marks the rendered button so an inline-edit input's blur handler keeps editing
   * alive when focus lands here (see {@link InlineTextField}), letting the button's
   * own {@link onClick} run instead of the input tearing it down first.
   */
  'data-keep-inline-edit'?: boolean;
};

const getTextAlignedPadding = (size: BaseButtonProps['size']) => {
  switch (size) {
    case 'tiny':
      return { x: 2, y: 2 };
    case 'small':
      return { x: 5, y: 4 };
    case 'large':
      return { x: 11, y: 8 };
    case 'medium':
    default:
      return { x: 8, y: 6 };
  }
};

export const BaseButton: React.FC<BaseButtonProps> = ({
  variant = 'text',
  size = 'medium',
  color = 'primary',
  disabled = false,
  type = 'button',
  fullWidth = false,
  textTransform = 'none',
  minWidth,
  startIcon,
  endIcon,
  id,
  onClick,
  link,
  children,
  'data-keep-inline-edit': keepInlineEdit,
}) => {
  const isTextAligned = variant === textAlignedVariant;
  const textAlignedPadding = getTextAlignedPadding(size);
  const muiVariant = isTextAligned ? 'text' : variant;
  const muiSize = size === 'tiny' ? 'small' : size;

  const sx = useObjectMemo({
    textTransform,
    minWidth,
    whiteSpace: 'nowrap',
    padding: size === 'tiny' ? '2px 2px' : undefined,
    margin: isTextAligned
      ? `-${textAlignedPadding.y}px -${textAlignedPadding.x}px`
      : undefined,
  } satisfies SxProps<Theme>);

  if (link) {
    return (
      <MuiButton
        id={id}
        variant={muiVariant}
        size={muiSize}
        color={color}
        disabled={disabled}
        type={type}
        startIcon={startIcon}
        endIcon={endIcon}
        component={RouterLink}
        to={link.to}
        replace={link.replace}
        target={link.target}
        rel={link.rel}
        onClick={onClick}
        sx={sx}
        fullWidth={fullWidth}
        data-keep-inline-edit={keepInlineEdit}
      >
        <span style={ghostButtonLabelStyles}>{children}</span>
      </MuiButton>
    );
  }
  return (
    <MuiButton
      id={id}
      variant={muiVariant}
      size={muiSize}
      color={color}
      disabled={disabled}
      type={type}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={onClick}
      sx={sx}
      fullWidth={fullWidth}
      data-keep-inline-edit={keepInlineEdit}
    >
      <span style={ghostButtonLabelStyles}>{children}</span>
    </MuiButton>
  );
};
